import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, BotIcon, SearchIcon, MessageSquareIcon, PencilIcon, CopyIcon, Trash2Icon, CpuIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useAgentStore } from '../stores/agentStore';
import { useModelStore } from '../stores/modelStore';
import { useUiStore } from '../stores/uiStore';
import { Agent } from '../types';
import { PageContainer, PageHeader, EmptyState, AgentStatusBadge } from '../components/common/page';
import { Card, Button, Badge, Input, Select } from '../components/common/ui';
import { useSessionStore } from '../stores/sessionStore';
import { formatRelative } from '../utils/format';
export function AgentCenterPage() {
  useScreenInit();
  const navigate = useNavigate();
  const agents = useAgentStore(s => s.agents);
  const {
    duplicateAgent,
    deleteAgent
  } = useAgentStore();
  const models = useModelStore(s => s.models);
  const startSession = useSessionStore(s => s.startSession);
  const pushToast = useUiStore(s => s.pushToast);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const handleUse = (a: Agent) => {
    const sessionId = startSession(a, a.defaultModelId);
    navigate(`/history/${sessionId}`);
  };
  const filtered = agents.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (query) {
      const hay = `${a.name}${a.description}${a.tags.join(' ')}${a.skills.map(s => s.name).join(' ')}`.toLowerCase();
      if (!hay.includes(query.toLowerCase())) return false;
    }
    return true;
  });
  const modelName = (id: string) => models.find(m => m.modelId === id)?.name || '未指定';
  const handleDelete = (a: Agent) => {
    if (confirm(`确认删除 Agent「${a.name}」？历史会话将无法恢复。`)) {
      deleteAgent(a.agentId);
      pushToast('success', 'Agent 已删除');
    }
  };
  return <PageContainer data-id="element-266">
      <PageHeader title="Agent 中心" description="查看并使用已创建的 Agent，点击「使用」在历史会话中新建对话" action={<Button onClick={() => navigate('/agents/new')} data-id="element-268">
            <PlusIcon className="h-4 w-4" data-id="element-269" /> 创建 Agent
          </Button>} data-id="element-267" />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row" data-id="element-270">
        <div className="relative flex-1" data-id="element-271">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" data-id="element-272" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="按名称、Skill、标签搜索" className="pl-9" data-id="element-273" />
        </div>
        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="sm:w-40" data-id="element-274">
          <option value="all" data-id="element-275">全部状态</option>
          <option value="active" data-id="element-276">已启用</option>
          <option value="draft" data-id="element-277">草稿</option>
          <option value="disabled" data-id="element-278">已禁用</option>
        </Select>
      </div>

      {filtered.length === 0 ? <EmptyState icon={BotIcon} title="暂无 Agent" description="基于提示词、Skills 和模型创建你的第一个 Agent" action={<Button onClick={() => navigate('/agents/new')} data-id="element-280">
              <PlusIcon className="h-4 w-4" data-id="element-281" /> 创建 Agent
            </Button>} data-id="element-279" /> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-282">
          {filtered.map(a => <Card key={a.agentId} className="flex flex-col p-4" data-id="element-283">
              <div className="flex items-start justify-between gap-2" data-id="element-284">
                <div className="flex items-center gap-2.5" data-id="element-285">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600/15" data-id="element-286">
                    <BotIcon className="h-5 w-5 text-indigo-400" data-id="element-287" />
                  </div>
                  <div data-id="element-288">
                    <div className="font-medium text-slate-100" data-id="element-289">{a.name}</div>
                    <div className="text-xs text-slate-500" data-id="element-290">
                      最近使用 {formatRelative(a.lastUsedAt)}
                    </div>
                  </div>
                </div>
                <AgentStatusBadge status={a.status} data-id="element-291" />
              </div>
              <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-400" data-id="element-292">
                {a.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-293">
                <Badge tone="indigo" data-id="element-294">
                  <CpuIcon className="h-3 w-3" data-id="element-295" /> {modelName(a.defaultModelId)}
                </Badge>
                {a.skills.map(s => <Badge key={s.skillId} tone="purple" data-id="element-296">
                    {s.name}
                  </Badge>)}
                {a.knowledgeBases?.map(kb => <Badge key={kb.knowledgeBaseId} tone="green" data-id="element-297">
                    {kb.name}
                  </Badge>)}
                {a.skills.length === 0 && (!a.knowledgeBases || a.knowledgeBases.length === 0) && <span className="text-xs text-slate-600" data-id="element-298">
                      无 Skills / 知识库
                    </span>}
              </div>
              <div className="mt-4 flex gap-2 border-t border-slate-800 pt-3" data-id="element-299">
                <Button size="sm" className="flex-1" onClick={() => handleUse(a)} data-id="element-300">
                  <MessageSquareIcon className="h-3.5 w-3.5" data-id="element-301" /> 使用
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/agents/${a.agentId}/edit`)} aria-label="编辑" data-id="element-302">
                  <PencilIcon className="h-3.5 w-3.5" data-id="element-303" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => {
            const id = duplicateAgent(a.agentId);
            if (id) pushToast('success', '已复制 Agent');
          }} aria-label="复制" data-id="element-304">
                  <CopyIcon className="h-3.5 w-3.5" data-id="element-305" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(a)} className="text-red-400 hover:text-red-300" aria-label="删除" data-id="element-306">
                  <Trash2Icon className="h-3.5 w-3.5" data-id="element-307" />
                </Button>
              </div>
            </Card>)}
        </div>}
    </PageContainer>;
}